// קובץ חדש: C:\gocode\projects\cartax\git\cartax_fe\src\components\shared\ActualCostCalculator.tsx

import React, { useState, useMemo } from "react";
import MotionCard from "./MotionCard";
import ExplanationTooltip from "./ExplanationTooltip";
import { ExplanationData } from "../../services/enduser/calculatorService";
// מיפוי סימבולי מטבעות
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  ILS: "₪",
  CHF: "Fr",
  JPY: "¥",
  AUD: "A$",
  CAD: "C$",
};

interface ActualCostCalculatorProps {
  // אישי או מסחרי
  mode?: "personal" | "commercial";

  // נתוני מטבע רכב והובלה (אותו מטבע)
  vehicleCurrency: string;
  vehicleExchangeRate: string;
  vehiclePriceForeign: string;
  shippingCostForeign: string;

  // נתוני מטבע ביטוח (עשוי להיות שונה)
  insuranceCurrency: string;
  insuranceExchangeRate: string;
  insuranceCostForeign: string;

  // הוצאות מקומיות (כבר בש"ח)
  localExpensesIls: string;

  // סך המיסים מהחישוב המקורי
  totalTaxes: number;

  // callback לעדכון הנתונים לשמירה
  onDataChange: (data: ActualCostData | null) => void;
  explanations?: Record<string, ExplanationData>;
  initialData?: ActualCostData | null;
}

export interface ActualCostData {
  isEnabled: boolean;
  // המטבע ושער המכס שהשערים בפועל נבנו מולם — לזיהוי שינוי אחרי "חזור לעריכה"
  vehicleCurrency?: string;
  insuranceCurrency?: string;
  vehicleBaseRate?: string;
  insuranceBaseRate?: string;
  actualVehicleRate: string;
  actualShippingRate: string;
  actualInsuranceRate: string;
  customsBrokerCost: string;
  bankFees: string;
  storageCost: string;
  unloadingCost: string;
  brokerFee: string;
  localTransportCost: string;
  testAndLicenseCost: string;
  calculatedResult: {
    vehicleValueIls: number;
    shippingValueIls: number;
    insuranceValueIls: number;
    localExpenses: number;
    totalTaxes: number;
    additionalCosts: number;
    totalActualCost: number;
  } | null;
}

/**
 * שער בפועל התחלתי כשהרכיב נטען מחדש (למשל אחרי "חזור לעריכה").
 * השער השמור נשמר רק אם הוא עדיין רלוונטי:
 * - המטבע הוחלף → השער השמור שייך למטבע אחר → שער המכס הנוכחי
 * - המשתמש לא שינה את השער בפועל (שווה לשער המכס שהיה) → ממשיך אחרי שער המכס הנוכחי
 * - אחרת (המשתמש הקליד שער משלו) → נשמר
 * רשומות ישנות (בלי מטבע/שער בסיס) מתנהגות כמו קודם — השער השמור נשמר.
 */
const getInitialActualRate = (
  savedRate: string | undefined,
  savedBaseRate: string | undefined,
  savedCurrency: string | undefined,
  currentCurrency: string,
  currentBaseRate: string
): string => {
  if (savedRate == null) return currentBaseRate;
  if (savedCurrency && savedCurrency !== currentCurrency) return currentBaseRate;
  if (
    savedBaseRate != null &&
    parseFloat(savedRate) === parseFloat(savedBaseRate)
  ) {
    return currentBaseRate;
  }
  return savedRate;
};

const ActualCostCalculator: React.FC<ActualCostCalculatorProps> = ({
  mode = "personal",
  vehicleCurrency,
  vehicleExchangeRate,
  vehiclePriceForeign,
  shippingCostForeign,
  insuranceCurrency,
  insuranceExchangeRate,
  insuranceCostForeign,
  localExpensesIls,
  totalTaxes,
  onDataChange,
  explanations = {},
  initialData,
}) => {
  const [isExpanded, setIsExpanded] = useState(
    initialData?.isEnabled ?? false
  );

  // שערים בפועל - דיפולט = initialData או השער המקורי (ראה getInitialActualRate)
  const [actualVehicleRate, setActualVehicleRate] = useState(() =>
    getInitialActualRate(
      initialData?.actualVehicleRate,
      initialData?.vehicleBaseRate,
      initialData?.vehicleCurrency,
      vehicleCurrency,
      vehicleExchangeRate
    )
  );
  const [actualShippingRate, setActualShippingRate] = useState(() =>
    getInitialActualRate(
      initialData?.actualShippingRate,
      initialData?.vehicleBaseRate,
      initialData?.vehicleCurrency,
      vehicleCurrency,
      vehicleExchangeRate
    )
  );
  const [actualInsuranceRate, setActualInsuranceRate] = useState(() =>
    getInitialActualRate(
      initialData?.actualInsuranceRate,
      initialData?.insuranceBaseRate,
      initialData?.insuranceCurrency,
      insuranceCurrency,
      insuranceExchangeRate
    )
  );

  // עלויות נוספות
  const [customsBrokerCost, setCustomsBrokerCost] = useState(
    initialData?.customsBrokerCost ?? ""
  );
  const [bankFees, setBankFees] = useState(
    initialData?.bankFees ?? ""
  );
  const [storageCost, setStorageCost] = useState(
    initialData?.storageCost ?? ""
  );
  const [unloadingCost, setUnloadingCost] = useState(
    initialData?.unloadingCost ?? ""
  );
  const [brokerFee, setBrokerFee] = useState(
    initialData?.brokerFee ?? ""
  );
  const [localTransportCost, setLocalTransportCost] = useState(
    initialData?.localTransportCost ?? ""
  );
  const [testAndLicenseCost, setTestAndLicenseCost] = useState(
    initialData?.testAndLicenseCost ?? ""
  );

  // האם יש ביטוח
  const hasInsurance = parseFloat(insuranceCostForeign || "0") > 0;

  // סימבולי מטבעות
  const vehicleSymbol = CURRENCY_SYMBOLS[vehicleCurrency] || vehicleCurrency;
  const insuranceSymbol =
    CURRENCY_SYMBOLS[insuranceCurrency] || insuranceCurrency;

  // חישוב התוצאה
  const calculatedResult = useMemo(() => {
    if (!isExpanded) return null;

    const vehiclePrice = parseFloat(vehiclePriceForeign || "0");
    const shippingCost = parseFloat(shippingCostForeign || "0");
    const insuranceCost = parseFloat(insuranceCostForeign || "0");
    const localExp = parseFloat(localExpensesIls || "0");

    const vehRate = parseFloat(actualVehicleRate || "0");
    const shipRate = parseFloat(actualShippingRate || "0");
    const insRate = parseFloat(actualInsuranceRate || "0");

    // חישוב ערכים בש"ח לפי השערים החדשים
    const vehicleValueIls = vehiclePrice * vehRate;
    const shippingValueIls = shippingCost * shipRate;
    const insuranceValueIls = hasInsurance ? insuranceCost * insRate : 0;

    // סכום עלויות נוספות
    const additionalCosts =
      parseFloat(customsBrokerCost || "0") +
      parseFloat(bankFees || "0") +
      parseFloat(storageCost || "0") +
      parseFloat(unloadingCost || "0") +
      (mode === "personal" ? parseFloat(brokerFee || "0") : 0) +
      parseFloat(localTransportCost || "0") +
      parseFloat(testAndLicenseCost || "0");

    // סה"כ עלות בפועל
    const totalActualCost =
      vehicleValueIls +
      shippingValueIls +
      insuranceValueIls +
      localExp +
      totalTaxes +
      additionalCosts;

    return {
      vehicleValueIls,
      shippingValueIls,
      insuranceValueIls,
      localExpenses: localExp,
      totalTaxes,
      additionalCosts,
      totalActualCost,
    };
  }, [
     isExpanded,
    mode,
    vehiclePriceForeign,
    shippingCostForeign,
    insuranceCostForeign,
    localExpensesIls,
    actualVehicleRate,
    actualShippingRate,
    actualInsuranceRate,
    customsBrokerCost,
    bankFees,
    storageCost,
    unloadingCost,
    brokerFee,
    localTransportCost,
    testAndLicenseCost,
    totalTaxes,
    hasInsurance,
  ]);

  // עדכון ה-parent בכל שינוי
  React.useEffect(() => {
    if (isExpanded && calculatedResult) {
      onDataChange({
        isEnabled: true,
        vehicleCurrency,
        insuranceCurrency,
        vehicleBaseRate: vehicleExchangeRate,
        insuranceBaseRate: insuranceExchangeRate,
        actualVehicleRate,
        actualShippingRate,
        actualInsuranceRate,
        customsBrokerCost,
        bankFees,
        storageCost,
        unloadingCost,
        brokerFee,
        localTransportCost,
        testAndLicenseCost,
        calculatedResult,
      });
    } else {
      onDataChange(null);
    }
  }, [
    isExpanded,
    calculatedResult,
    vehicleCurrency,
    insuranceCurrency,
    vehicleExchangeRate,
    insuranceExchangeRate,
    actualVehicleRate,
    actualShippingRate,
    actualInsuranceRate,
    customsBrokerCost,
    bankFees,
    storageCost,
    unloadingCost,
    brokerFee,
    localTransportCost,
    testAndLicenseCost,
    onDataChange,
  ]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("he-IL", {
      style: "currency",
      currency: "ILS",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <MotionCard className="p-6 border-2 border-blue-200 bg-blue-50 mb-6">
      {/* כותרת עם כפתור פתיחה/סגירה */}
      <button
        onClick={handleToggle}
        className="w-full flex items-center justify-between text-right"
      >
        <h3 className="font-bold text-lg text-blue-800">
          🎯 דייק לי את התוצאה
        </h3>
        <span className="text-2xl text-blue-600">{isExpanded ? "▲" : "▼"}</span>
      </button>

      {!isExpanded && (
        <p className="text-sm text-blue-600 mt-2">
          לחץ כאן לחישוב עלות הרכב בפועל כולל כל ההוצאות הנלוות
        </p>
      )}

      {isExpanded && (
        <div className="mt-6 space-y-6">
          {/* הסבר */}
          <p className="text-sm text-blue-700 bg-blue-100 p-3 rounded-lg">
            💡 כאן תוכל לחשב את העלות האמיתית של הרכב, כולל הפרשי שערים ועלויות
            נלוות.
            <br />
            <span className="font-medium">שים לב:</span> חישוב זה לא משפיע על
            סכום המיסים - הוא רק לידיעתך.
          </p>

          {/* שערי מט"ח בפועל */}
          <div className="bg-white p-4 rounded-lg border border-blue-200">
            <h4 className="font-bold text-blue-800 mb-4">💱 שערי מט"ח בפועל</h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* שער רכב */}
              <div>
                <label
                  htmlFor="actual_vehicle_rate"
                  className="flex items-center text-sm font-medium mb-1"
                >
                  מה שער ה-{vehicleSymbol} שקנית את הרכב בפועל?
                  <ExplanationTooltip stepKey="actual_vehicle_rate" explanations={explanations} />
                </label>
                <input
                  id="actual_vehicle_rate"
                  type="number"
                  step="0.0001"
                  min="0"
                  value={actualVehicleRate}
                  onChange={(e) => setActualVehicleRate(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  placeholder={vehicleExchangeRate}
                />
              </div>

              {/* שער הובלה */}
              <div>
                <label
                  htmlFor="actual_shipping_rate"
                  className="flex items-center text-sm font-medium mb-1"
                >
                  מה שער ה-{vehicleSymbol} שקנית את ההובלה בפועל?
                  <ExplanationTooltip stepKey="actual_shipping_rate" explanations={explanations} />
                </label>
                <input
                  id="actual_shipping_rate"
                  type="number"
                  step="0.0001"
                  min="0"
                  value={actualShippingRate}
                  onChange={(e) => setActualShippingRate(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  placeholder={vehicleExchangeRate}
                />
              </div>

              {/* שער ביטוח - רק אם יש ביטוח */}
              {hasInsurance && (
                <div>
                  <label
                    htmlFor="actual_insurance_rate"
                    className="flex items-center text-sm font-medium mb-1"
                  >
                    מה שער ה-{insuranceSymbol} שקנית את הביטוח בפועל?
                    <ExplanationTooltip stepKey="actual_insurance_rate" explanations={explanations} />
                  </label>
                  <input
                    id="actual_insurance_rate"
                    type="number"
                    step="0.0001"
                    min="0"
                    value={actualInsuranceRate}
                    onChange={(e) => setActualInsuranceRate(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    placeholder={insuranceExchangeRate}
                  />
                </div>
              )}
            </div>
          </div>

          {/* עלויות נוספות */}
          <div className="bg-white p-4 rounded-lg border border-blue-200">
            <h4 className="font-bold text-blue-800 mb-4">
              {" "}
              עלויות נלוות (בש"ח)
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="customs_broker_cost"
                  className="flex items-center text-sm font-medium mb-1"
                >
                  עלות עמילות מכס
                  <ExplanationTooltip stepKey="customs_broker_cost" explanations={explanations} />
                </label>
                <input
                  id="customs_broker_cost"
                  type="number"
                  min="0"
                  value={customsBrokerCost}
                  onChange={(e) => setCustomsBrokerCost(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  placeholder="0"
                />
              </div>

              <div>
                <label
                  htmlFor="bank_fees"
                  className="flex items-center text-sm font-medium mb-1"
                >
                  עמלות בנק
                  <ExplanationTooltip stepKey="bank_fees" explanations={explanations} />
                </label>
                <input
                  id="bank_fees"
                  type="number"
                  min="0"
                  value={bankFees}
                  onChange={(e) => setBankFees(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  placeholder="0"
                />
              </div>

              <div>
                <label
                  htmlFor="storage_cost"
                  className="flex items-center text-sm font-medium mb-1"
                >
                  {mode === "commercial" ? "אחסנה והשהייה" : "עלות אחסנה"}
                  <ExplanationTooltip stepKey={mode === "commercial" ? "commercial_storage_cost" : "storage_cost"} explanations={explanations} />
                </label>
                <input
                  id="storage_cost"
                  type="number"
                  min="0"
                  value={storageCost}
                  onChange={(e) => setStorageCost(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  placeholder="0"
                />
              </div>

              <div>
                <label
                  htmlFor="unloading_cost"
                  className="flex items-center text-sm font-medium mb-1"
                >
                  עלות ריקון ממחסן
                  <ExplanationTooltip stepKey="unloading_cost" explanations={explanations} />
                </label>
                <input
                  id="unloading_cost"
                  type="number"
                  min="0"
                  value={unloadingCost}
                  onChange={(e) => setUnloadingCost(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  placeholder="0"
                />
              </div>

              {mode === "personal" && (
              <div>
                <label
                  htmlFor="broker_fee"
                  className="flex items-center text-sm font-medium mb-1"
                >
                  דמי מתווך בייבוא אישי
                  <ExplanationTooltip stepKey="broker_fee" explanations={explanations} />
                </label>
                <input
                  id="broker_fee"
                  type="number"
                  min="0"
                  value={brokerFee}
                  onChange={(e) => setBrokerFee(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  placeholder="0"
                />
              </div>
              )}

              <div>
                <label
                  htmlFor="local_transport_cost"
                  className="flex items-center text-sm font-medium mb-1"
                >
                  {mode === "commercial" ? "הובלה בארץ" : "הובלה לאחר שחרור ממכס"}
                  <ExplanationTooltip stepKey={mode === "commercial" ? "commercial_local_transport_cost" : "local_transport_cost"} explanations={explanations} />
                </label>
                <input
                  id="local_transport_cost"
                  type="number"
                  min="0"
                  value={localTransportCost}
                  onChange={(e) => setLocalTransportCost(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  placeholder="0"
                />
              </div>

              <div>
                <label
                  htmlFor="test_and_license_cost"
                  className="flex items-center text-sm font-medium mb-1"
                >
                  {mode === "commercial" ? "התקנות טסט ורישוי" : "טסט ורישוי לרכב"}
                  <ExplanationTooltip stepKey={mode === "commercial" ? "commercial_test_and_license_cost" : "test_and_license_cost"} explanations={explanations} />
                </label>
                <input
                  id="test_and_license_cost"
                  type="number"
                  min="0"
                  value={testAndLicenseCost}
                  onChange={(e) => setTestAndLicenseCost(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  placeholder="0"
                />
              </div>
            </div>
          </div>

          {/* טבלת תוצאות */}
          {calculatedResult && (
            <div className="bg-white p-4 rounded-lg border-2 border-blue-300">
              <h4 className="font-bold text-blue-800 mb-4">
                📊 עלות הרכב בפועל
              </h4>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-1 border-b border-gray-100">
                  <span>ערך הרכב בש"ח (לפי שער בפועל):</span>
                  <span className="font-medium">
                    {formatCurrency(calculatedResult.vehicleValueIls)}
                  </span>
                </div>

                <div className="flex justify-between py-1 border-b border-gray-100">
                  <span>ערך ההובלה בש"ח (לפי שער בפועל):</span>
                  <span className="font-medium">
                    {formatCurrency(calculatedResult.shippingValueIls)}
                  </span>
                </div>

                {hasInsurance && (
                  <div className="flex justify-between py-1 border-b border-gray-100">
                    <span>ערך הביטוח בש"ח (לפי שער בפועל):</span>
                    <span className="font-medium">
                      {formatCurrency(calculatedResult.insuranceValueIls)}
                    </span>
                  </div>
                )}

                <div className="flex justify-between py-1 border-b border-gray-100">
                  <span>הוצאות מקומיות:</span>
                  <span className="font-medium">
                    {formatCurrency(calculatedResult.localExpenses)}
                  </span>
                </div>

                <div className="flex justify-between py-1 border-b border-gray-100">
                  <span>סך המיסים:</span>
                  <span className="font-medium">
                    {formatCurrency(calculatedResult.totalTaxes)}
                  </span>
                </div>

                {calculatedResult.additionalCosts > 0 && (
                  <div className="flex justify-between py-1 border-b border-gray-100">
                    <span>עלויות נלוות:</span>
                    <span className="font-medium">
                      {formatCurrency(calculatedResult.additionalCosts)}
                    </span>
                  </div>
                )}

                <div className="flex justify-between py-3 mt-2 bg-blue-100 rounded-lg px-3 text-lg font-bold">
                  <span className="text-blue-800">סה"כ עלות הרכב בפועל:</span>
                  <span className="text-blue-900">
                    {formatCurrency(calculatedResult.totalActualCost)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </MotionCard>
  );
};

export default ActualCostCalculator;
